import type { GeoEntity, TrackPoint } from '@/stores/geolocation-store'

export const SAMPLE_ENTITIES: GeoEntity[] = [
  {
    id: 'sample-1',
    name: 'Vehicle Alpha',
    type: 'vehicle',
    latitude: 50.0906,
    longitude: 14.4494,
    properties: { plate: '1A2 3456', color: 'black', model: 'Škoda Octavia' },
  },
  {
    id: 'sample-2',
    name: 'Vehicle Bravo',
    type: 'vehicle',
    latitude: 50.0875,
    longitude: 14.4276,
    properties: { plate: '2B3 7890', color: 'silver', model: 'VW Passat' },
  },
  {
    id: 'sample-3',
    name: 'Vehicle Charlie',
    type: 'vehicle',
    latitude: 50.0652,
    longitude: 14.4195,
    properties: { plate: '3C4 5678', color: 'white', model: 'BMW 3' },
  },
  {
    id: 'sample-4',
    name: 'Vehicle Delta',
    type: 'vehicle',
    latitude: 50.1008,
    longitude: 14.4218,
    properties: { plate: '4D5 1234', color: 'blue', model: 'Audi A4' },
  },
  {
    id: 'sample-5',
    name: 'Vehicle Echo',
    type: 'vehicle',
    latitude: 50.0781,
    longitude: 14.4058,
    properties: { plate: '5E6 9012', color: 'red', model: 'Škoda Superb' },
  },
  {
    id: 'sample-aircraft-1',
    name: 'Cessna 172 (OM-TAT)',
    type: 'Aircraft',
    latitude: 49.0733,
    longitude: 20.2411,
    properties: { registration: 'OM-TAT', model: 'Cessna 172S', callsign: 'OMTAT' },
  },
  {
    id: 'sample-aircraft-2',
    name: 'A320neo (OK-TVA)',
    type: 'Aircraft',
    latitude: 50.7854,
    longitude: 6.9028,
    properties: { registration: 'OK-TVA', model: 'Airbus A320neo', callsign: 'CSA123' },
  },
  {
    id: 'sample-aircraft-3',
    name: 'B737-800 (TC-JFL)',
    type: 'Aircraft',
    latitude: 45.6543,
    longitude: 18.6612,
    properties: { registration: 'TC-JFL', model: 'Boeing 737-800', callsign: 'THY1956' },
  },
  {
    id: 'sample-aircraft-4',
    name: 'A350-900 (F-HTYA)',
    type: 'Aircraft',
    latitude: 37.1315,
    longitude: 28.9568,
    properties: { registration: 'F-HTYA', model: 'Airbus A350-900', callsign: 'AFR218' },
  },
  {
    id: 'sample-aircraft-5',
    name: 'B787-9 (PH-BHA)',
    type: 'Aircraft',
    latitude: 46.4749,
    longitude: -34.5071,
    properties: { registration: 'PH-BHA', model: 'Boeing 787-9', callsign: 'KLM643' },
  },
  {
    id: 'sample-aircraft-6',
    name: 'A319 (I-BIMA)',
    type: 'Aircraft',
    latitude: 39.8683,
    longitude: 18.0917,
    properties: { registration: 'I-BIMA', model: 'Airbus A319', callsign: 'AZA704' },
  },
  {
    id: 'sample-aircraft-7',
    name: 'E190 (OE-LWA)',
    type: 'Aircraft',
    latitude: 52.0414,
    longitude: 26.9922,
    properties: { registration: 'OE-LWA', model: 'Embraer E190', callsign: 'AUA351' },
  },
  {
    id: 'sample-aircraft-8',
    name: 'ATR 72-600 (EC-MVI)',
    type: 'Aircraft',
    latitude: 39.6375,
    longitude: -6.3514,
    properties: { registration: 'EC-MVI', model: 'ATR 72-600', callsign: 'BZZ801' },
  },
  {
    id: 'sample-aircraft-9',
    name: 'A321neo (HB-JPA)',
    type: 'Aircraft',
    latitude: 44.3809,
    longitude: 5.3139,
    properties: { registration: 'HB-JPA', model: 'Airbus A321neo', callsign: 'SWR416' },
  },
  {
    id: 'sample-aircraft-10',
    name: 'A350-900 (OH-LWA)',
    type: 'Aircraft',
    latitude: 48.0409,
    longitude: 82.6749,
    properties: { registration: 'OH-LWA', model: 'Airbus A350-900', callsign: 'FIN79' },
  },
  {
    id: 'sample-aircraft-11',
    name: 'B777-300ER (G-STBA)',
    type: 'Aircraft',
    latitude: 26.4172,
    longitude: 51.7686,
    properties: { registration: 'G-STBA', model: 'Boeing 777-300ER', callsign: 'BAW15' },
  },
]

function track(points: [number, number, string][]): TrackPoint[] {
  return points.map(([latitude, longitude, timestamp]) => ({ latitude, longitude, timestamp }))
}

function trackWithAltitude(points: [number, number, number, string][]): TrackPoint[] {
  return points.map(([latitude, longitude, altitude, timestamp]) => ({
    latitude,
    longitude,
    altitude,
    timestamp,
  }))
}

// Vehicle Alpha: OSRM road-snapped route through Prague
// Žižkov → Seifertova → Hlavní nádraží → I.P. Pavlova → Ječná → Karlovo nám. →
// Resslova → Národní → Old Town → Pařížská → Čechův most → Letná → Holešovice → back
export const SAMPLE_TRACKS: Record<string, TrackPoint[]> = {
  'sample-1': track([
    [50.090583, 14.449378, '2026-03-23T08:00:00Z'],
    [50.089909, 14.448104, '2026-03-23T08:00:17Z'],
    [50.090603, 14.447529, '2026-03-23T08:00:29Z'],
    [50.091503, 14.447043, '2026-03-23T08:00:41Z'],
    [50.092598, 14.446492, '2026-03-23T08:00:56Z'],
    [50.093524, 14.446142, '2026-03-23T08:01:08Z'],
    [50.09388, 14.444499, '2026-03-23T08:01:24Z'],
    [50.09413, 14.442702, '2026-03-23T08:01:40Z'],
    [50.094132, 14.440989, '2026-03-23T08:01:54Z'],
    [50.094056, 14.439097, '2026-03-23T08:02:09Z'],
    [50.093392, 14.438241, '2026-03-23T08:02:25Z'],
    [50.092487, 14.438419, '2026-03-23T08:02:37Z'],
    [50.091571, 14.438121, '2026-03-23T08:02:49Z'],
    [50.090857, 14.437875, '2026-03-23T08:03:01Z'],
    [50.090486, 14.43657, '2026-03-23T08:03:12Z'],
    [50.090132, 14.435273, '2026-03-23T08:03:24Z'],
    [50.089864, 14.433571, '2026-03-23T08:03:39Z'],
    [50.089457, 14.432364, '2026-03-23T08:03:51Z'],
    [50.088491, 14.432406, '2026-03-23T08:04:03Z'],
    [50.088359, 14.431833, '2026-03-23T08:04:17Z'],
    [50.088572, 14.433236, '2026-03-23T08:04:29Z'],
    [50.088628, 14.434766, '2026-03-23T08:04:42Z'],
    [50.088654, 14.436529, '2026-03-23T08:04:57Z'],
    [50.089495, 14.437243, '2026-03-23T08:05:09Z'],
    [50.090356, 14.437195, '2026-03-23T08:05:21Z'],
    [50.091153, 14.436476, '2026-03-23T08:05:33Z'],
    [50.092352, 14.435861, '2026-03-23T08:05:50Z'],
    [50.092352, 14.435861, '2026-03-23T08:06:20Z'], // stopped at light
    [50.092544, 14.436879, '2026-03-23T08:06:31Z'],
    [50.091664, 14.437504, '2026-03-23T08:06:44Z'],
    [50.090702, 14.437629, '2026-03-23T08:06:57Z'],
    [50.089605, 14.437694, '2026-03-23T08:07:11Z'],
    [50.088583, 14.437686, '2026-03-23T08:07:24Z'],
    [50.087556, 14.437607, '2026-03-23T08:07:37Z'],
    [50.086646, 14.437337, '2026-03-23T08:07:49Z'],
    [50.085734, 14.436715, '2026-03-23T08:08:02Z'],
    [50.084781, 14.435896, '2026-03-23T08:08:16Z'],
    [50.083926, 14.435203, '2026-03-23T08:08:28Z'],
    [50.082868, 14.434447, '2026-03-23T08:08:43Z'],
    [50.081752, 14.433638, '2026-03-23T08:08:59Z'],
    [50.080843, 14.432472, '2026-03-23T08:09:15Z'],
    [50.079972, 14.430964, '2026-03-23T08:09:31Z'],
    [50.079222, 14.430014, '2026-03-23T08:09:44Z'],
    [50.07802, 14.429413, '2026-03-23T08:10:00Z'],
    [50.077171, 14.429004, '2026-03-23T08:10:13Z'],
    [50.07723, 14.427123, '2026-03-23T08:10:29Z'],
    [50.077291, 14.425483, '2026-03-23T08:10:42Z'],
    [50.077356, 14.423955, '2026-03-23T08:10:55Z'],
    [50.077448, 14.422364, '2026-03-23T08:11:08Z'],
    [50.07781, 14.421329, '2026-03-23T08:11:21Z'],
    [50.078616, 14.42181, '2026-03-23T08:11:33Z'],
    [50.078586, 14.424345, '2026-03-23T08:11:54Z'],
    [50.07983, 14.423696, '2026-03-23T08:12:11Z'],
    [50.079606, 14.422806, '2026-03-23T08:12:24Z'],
    [50.079606, 14.422806, '2026-03-23T08:12:54Z'], // stopped at light
    [50.078932, 14.421919, '2026-03-23T08:13:06Z'],
    [50.07803, 14.421442, '2026-03-23T08:13:18Z'],
    [50.077649, 14.419947, '2026-03-23T08:13:36Z'],
    [50.077584, 14.41846, '2026-03-23T08:13:49Z'],
    [50.077575, 14.416523, '2026-03-23T08:14:07Z'],
    [50.077456, 14.415118, '2026-03-23T08:14:19Z'],
    [50.078663, 14.41423, '2026-03-23T08:14:41Z'],
    [50.077542, 14.414241, '2026-03-23T08:14:56Z'],
    [50.076423, 14.414071, '2026-03-23T08:15:11Z'],
    [50.07579, 14.413305, '2026-03-23T08:15:23Z'],
    [50.075473, 14.410078, '2026-03-23T08:15:50Z'],
    [50.075479, 14.408632, '2026-03-23T08:16:02Z'],
    [50.074631, 14.407789, '2026-03-23T08:16:21Z'],
    [50.074769, 14.408914, '2026-03-23T08:16:32Z'],
    [50.076127, 14.408536, '2026-03-23T08:16:50Z'],
    [50.077042, 14.408274, '2026-03-23T08:17:02Z'],
    [50.078146, 14.408149, '2026-03-23T08:17:17Z'],
    [50.079592, 14.40805, '2026-03-23T08:17:35Z'],
    [50.081069, 14.407953, '2026-03-23T08:17:54Z'],
    [50.081318, 14.410044, '2026-03-23T08:18:14Z'],
    [50.08139, 14.412788, '2026-03-23T08:18:37Z'],
    [50.080583, 14.413352, '2026-03-23T08:18:50Z'],
    [50.079734, 14.413817, '2026-03-23T08:19:01Z'],
    [50.080572, 14.41392, '2026-03-23T08:19:13Z'],
    [50.081524, 14.413746, '2026-03-23T08:19:26Z'],
    [50.082476, 14.413887, '2026-03-23T08:19:38Z'],
    [50.082942, 14.41471, '2026-03-23T08:19:49Z'],
    [50.082942, 14.41471, '2026-03-23T08:20:19Z'], // stopped at light
    [50.083359, 14.416091, '2026-03-23T08:20:32Z'],
    [50.083771, 14.417405, '2026-03-23T08:20:44Z'],
    [50.083931, 14.41845, '2026-03-23T08:20:58Z'],
    [50.084816, 14.418915, '2026-03-23T08:21:10Z'],
    [50.085452, 14.418294, '2026-03-23T08:21:23Z'],
    [50.086394, 14.417723, '2026-03-23T08:21:36Z'],
    [50.087258, 14.417271, '2026-03-23T08:21:48Z'],
    [50.087879, 14.416602, '2026-03-23T08:22:00Z'],
    [50.088607, 14.415843, '2026-03-23T08:22:13Z'],
    [50.089462, 14.415895, '2026-03-23T08:22:25Z'],
    [50.090344, 14.416297, '2026-03-23T08:22:37Z'],
    [50.091427, 14.417748, '2026-03-23T08:22:56Z'],
    [50.092263, 14.417613, '2026-03-23T08:23:09Z'],
    [50.093697, 14.416666, '2026-03-23T08:23:29Z'],
    [50.093462, 14.414945, '2026-03-23T08:23:46Z'],
    [50.093158, 14.413527, '2026-03-23T08:23:58Z'],
    [50.092414, 14.413847, '2026-03-23T08:24:10Z'],
    [50.093214, 14.413768, '2026-03-23T08:24:22Z'],
    [50.093724, 14.41628, '2026-03-23T08:24:44Z'],
    [50.094007, 14.417615, '2026-03-23T08:24:56Z'],
    [50.094298, 14.41899, '2026-03-23T08:25:08Z'],
    [50.094498, 14.420495, '2026-03-23T08:25:21Z'],
    [50.094698, 14.422339, '2026-03-23T08:25:36Z'],
    [50.094909, 14.423743, '2026-03-23T08:25:48Z'],
    [50.095241, 14.425383, '2026-03-23T08:26:03Z'],
    [50.095415, 14.42675, '2026-03-23T08:26:15Z'],
    [50.095415, 14.42675, '2026-03-23T08:26:45Z'], // stopped at light
    [50.096309, 14.426256, '2026-03-23T08:26:57Z'],
    [50.097082, 14.423904, '2026-03-23T08:27:19Z'],
    [50.097677, 14.422817, '2026-03-23T08:27:31Z'],
    [50.098801, 14.422226, '2026-03-23T08:27:47Z'],
    [50.09973, 14.421738, '2026-03-23T08:27:59Z'],
    [50.10015, 14.422981, '2026-03-23T08:28:11Z'],
    [50.100654, 14.424741, '2026-03-23T08:28:27Z'],
    [50.100177, 14.423051, '2026-03-23T08:28:45Z'],
    [50.099859, 14.421659, '2026-03-23T08:28:58Z'],
    [50.098801, 14.422226, '2026-03-23T08:29:14Z'],
    [50.097859, 14.422687, '2026-03-23T08:29:26Z'],
    [50.097178, 14.423604, '2026-03-23T08:29:38Z'],
    [50.096479, 14.425946, '2026-03-23T08:29:59Z'],
    [50.095644, 14.42665, '2026-03-23T08:30:12Z'],
    [50.093411, 14.427097, '2026-03-23T08:30:41Z'],
    [50.092618, 14.427431, '2026-03-23T08:30:53Z'],
    [50.093089, 14.428386, '2026-03-23T08:31:06Z'],
    [50.093319, 14.429644, '2026-03-23T08:31:20Z'],
    [50.093471, 14.431823, '2026-03-23T08:31:38Z'],
    [50.092797, 14.432735, '2026-03-23T08:31:50Z'],
    [50.092552, 14.434896, '2026-03-23T08:32:09Z'],
    [50.091946, 14.436072, '2026-03-23T08:32:24Z'],
    [50.091009, 14.436581, '2026-03-23T08:32:37Z'],
    [50.089955, 14.437475, '2026-03-23T08:32:53Z'],
    [50.089598, 14.437885, '2026-03-23T08:33:04Z'],
    [50.089897, 14.439318, '2026-03-23T08:33:17Z'],
    [50.090041, 14.439979, '2026-03-23T08:33:23Z'],
  ]),

  // Cessna 172 (OM-TAT): Poprad-Tatry Airport → High Tatras scenic loop → return
  'sample-aircraft-1': trackWithAltitude([
    [49.0733, 20.2411, 700, '2026-03-23T09:00:00Z'],
    [49.0745, 20.239, 720, '2026-03-23T09:01:30Z'],
    [49.078, 20.235, 850, '2026-03-23T09:03:00Z'],
    [49.083, 20.228, 1100, '2026-03-23T09:04:30Z'],
    [49.09, 20.22, 1450, '2026-03-23T09:06:00Z'],
    [49.098, 20.21, 1800, '2026-03-23T09:07:30Z'],
    [49.107, 20.198, 2100, '2026-03-23T09:09:00Z'],
    [49.115, 20.185, 2400, '2026-03-23T09:10:30Z'],
    [49.124, 20.172, 2700, '2026-03-23T09:12:00Z'],
    [49.134, 20.158, 2900, '2026-03-23T09:13:30Z'],
    [49.145, 20.143, 3050, '2026-03-23T09:15:00Z'],
    [49.156, 20.135, 3150, '2026-03-23T09:16:30Z'],
    [49.1642, 20.1341, 3200, '2026-03-23T09:18:00Z'], // near Gerlachovsky stit
    [49.172, 20.14, 3150, '2026-03-23T09:19:30Z'],
    [49.18, 20.155, 3100, '2026-03-23T09:21:00Z'],
    [49.187, 20.175, 3050, '2026-03-23T09:22:30Z'],
    [49.192, 20.195, 3100, '2026-03-23T09:24:00Z'],
    [49.1952, 20.2131, 3200, '2026-03-23T09:25:30Z'], // near Lomnicky stit
    [49.193, 20.23, 3150, '2026-03-23T09:27:00Z'],
    [49.188, 20.245, 3050, '2026-03-23T09:28:30Z'],
    [49.181, 20.255, 2950, '2026-03-23T09:30:00Z'],
    [49.173, 20.258, 2900, '2026-03-23T09:31:30Z'],
    [49.165, 20.25, 2850, '2026-03-23T09:33:00Z'],
    [49.158, 20.235, 2900, '2026-03-23T09:34:30Z'],
    [49.152, 20.215, 2950, '2026-03-23T09:36:00Z'],
    [49.15, 20.195, 3000, '2026-03-23T09:37:30Z'],
    [49.153, 20.175, 3050, '2026-03-23T09:39:00Z'],
    [49.158, 20.155, 3100, '2026-03-23T09:40:30Z'],
    [49.162, 20.13, 3050, '2026-03-23T09:42:00Z'],
    [49.164, 20.105, 2950, '2026-03-23T09:43:30Z'],
    [49.1646, 20.085, 2900, '2026-03-23T09:45:00Z'],
    [49.1646, 19.9836, 3100, '2026-03-23T09:48:00Z'], // near Krivan
    [49.16, 19.995, 3050, '2026-03-23T09:49:30Z'],
    [49.153, 20.01, 2950, '2026-03-23T09:51:00Z'],
    [49.145, 20.03, 2850, '2026-03-23T09:52:30Z'],
    [49.135, 20.055, 2700, '2026-03-23T09:54:00Z'],
    [49.125, 20.08, 2500, '2026-03-23T09:55:30Z'],
    [49.115, 20.105, 2300, '2026-03-23T09:57:00Z'],
    [49.105, 20.13, 2050, '2026-03-23T09:58:30Z'],
    [49.097, 20.155, 1800, '2026-03-23T10:00:00Z'],
    [49.09, 20.175, 1500, '2026-03-23T10:01:30Z'],
    [49.085, 20.195, 1200, '2026-03-23T10:03:00Z'],
    [49.081, 20.21, 950, '2026-03-23T10:04:30Z'],
    [49.0775, 20.225, 800, '2026-03-23T10:06:00Z'],
    [49.075, 20.235, 740, '2026-03-23T10:07:30Z'],
    [49.0733, 20.2411, 700, '2026-03-23T10:09:00Z'], // landed at Poprad
  ]),

  'sample-aircraft-2': trackWithAltitude([
    [50.1017, 14.2608, 370, '2026-03-23T06:00:00Z'],
    [50.129, 14.083, 416, '2026-03-23T06:01:30Z'],
    [50.1562, 13.9039, 478, '2026-03-23T06:03:00Z'],
    [50.1837, 13.7265, 650, '2026-03-23T06:04:30Z'],
    [50.2125, 13.5472, 1156, '2026-03-23T06:06:00Z'],
    [50.2411, 13.3715, 2378, '2026-03-23T06:07:30Z'],
    [50.265, 13.1921, 4685, '2026-03-23T06:09:00Z'],
    [50.292, 13.0129, 7541, '2026-03-23T06:10:30Z'],
    [50.3221, 12.8335, 9640, '2026-03-23T06:12:00Z'],
    [50.3422, 12.6542, 10665, '2026-03-23T06:13:30Z'],
    [50.3711, 12.4762, 11073, '2026-03-23T06:15:00Z'],
    [50.3985, 12.2967, 11216, '2026-03-23T06:16:30Z'],
    [50.4225, 12.1157, 11299, '2026-03-23T06:18:00Z'],
    [50.4469, 11.935, 11302, '2026-03-23T06:19:30Z'],
    [50.4715, 11.7551, 11304, '2026-03-23T06:21:00Z'],
    [50.4978, 11.5753, 11293, '2026-03-23T06:22:30Z'],
    [50.519, 11.3949, 11300, '2026-03-23T06:24:00Z'],
    [50.5462, 11.2151, 11297, '2026-03-23T06:25:30Z'],
    [50.5686, 11.0332, 11300, '2026-03-23T06:27:00Z'],
    [50.5927, 10.8536, 11302, '2026-03-23T06:28:30Z'],
    [50.6156, 10.6706, 11296, '2026-03-23T06:30:00Z'],
    [50.6373, 10.4887, 11300, '2026-03-23T06:31:30Z'],
    [50.6616, 10.3069, 11295, '2026-03-23T06:33:00Z'],
    [50.6831, 10.1287, 11294, '2026-03-23T06:34:30Z'],
    [50.7035, 9.9437, 11298, '2026-03-23T06:36:00Z'],
    [50.7257, 9.7623, 11294, '2026-03-23T06:37:30Z'],
    [50.7458, 9.58, 11296, '2026-03-23T06:39:00Z'],
    [50.7693, 9.3972, 11301, '2026-03-23T06:40:30Z'],
    [50.789, 9.2135, 11294, '2026-03-23T06:42:00Z'],
    [50.8106, 9.0296, 11301, '2026-03-23T06:43:30Z'],
    [50.8304, 8.8501, 11291, '2026-03-23T06:45:00Z'],
    [50.8503, 8.6663, 11294, '2026-03-23T06:46:30Z'],
    [50.8717, 8.4815, 11295, '2026-03-23T06:48:00Z'],
    [50.8916, 8.3004, 11304, '2026-03-23T06:49:30Z'],
    [50.9089, 8.1144, 11288, '2026-03-23T06:51:00Z'],
    [50.9282, 7.9338, 11298, '2026-03-23T06:52:30Z'],
    [50.9456, 7.7481, 11299, '2026-03-23T06:54:00Z'],
    [50.9654, 7.5654, 11305, '2026-03-23T06:55:30Z'],
    [50.9812, 7.3805, 11300, '2026-03-23T06:57:00Z'],
    [50.9998, 7.1936, 11294, '2026-03-23T06:58:30Z'],
    [51.0187, 7.0121, 11300, '2026-03-23T07:00:00Z'],
    [51.0356, 6.8266, 11287, '2026-03-23T07:01:30Z'],
    [51.0503, 6.6407, 11302, '2026-03-23T07:03:00Z'],
    [51.0682, 6.4556, 11296, '2026-03-23T07:04:30Z'],
    [51.0848, 6.2718, 11300, '2026-03-23T07:06:00Z'],
    [51.0991, 6.0873, 11307, '2026-03-23T07:07:30Z'],
    [51.1172, 5.9013, 11298, '2026-03-23T07:09:00Z'],
    [51.1306, 5.7154, 11290, '2026-03-23T07:10:30Z'],
    [51.1461, 5.5289, 11298, '2026-03-23T07:12:00Z'],
    [51.1613, 5.3459, 11294, '2026-03-23T07:13:30Z'],
    [51.1748, 5.1599, 11308, '2026-03-23T07:15:00Z'],
    [51.1902, 4.9748, 11301, '2026-03-23T07:16:30Z'],
    [51.2017, 4.7894, 11306, '2026-03-23T07:18:00Z'],
    [51.2165, 4.6016, 11304, '2026-03-23T07:19:30Z'],
    [51.2294, 4.4163, 11306, '2026-03-23T07:21:00Z'],
    [51.2422, 4.2298, 11297, '2026-03-23T07:22:30Z'],
    [51.2542, 4.0431, 11297, '2026-03-23T07:24:00Z'],
    [51.2688, 3.8566, 11298, '2026-03-23T07:25:30Z'],
    [51.2792, 3.6706, 11301, '2026-03-23T07:27:00Z'],
    [51.2913, 3.4818, 11297, '2026-03-23T07:28:30Z'],
    [51.3014, 3.2973, 11303, '2026-03-23T07:30:00Z'],
    [51.3156, 3.1104, 11298, '2026-03-23T07:31:30Z'],
    [51.3233, 2.9227, 11288, '2026-03-23T07:33:00Z'],
    [51.3354, 2.7371, 11308, '2026-03-23T07:34:30Z'],
    [51.3451, 2.5496, 11298, '2026-03-23T07:36:00Z'],
    [51.3562, 2.3615, 11304, '2026-03-23T07:37:30Z'],
    [51.3655, 2.1757, 11301, '2026-03-23T07:39:00Z'],
    [51.374, 1.9863, 11263, '2026-03-23T07:40:30Z'],
    [51.3836, 1.7998, 11203, '2026-03-23T07:42:00Z'],
    [51.3912, 1.613, 11036, '2026-03-23T07:43:30Z'],
    [51.4017, 1.4272, 10664, '2026-03-23T07:45:00Z'],
    [51.4087, 1.2356, 9774, '2026-03-23T07:46:30Z'],
    [51.4184, 1.0482, 8031, '2026-03-23T07:48:00Z'],
    [51.4249, 0.8623, 5488, '2026-03-23T07:49:30Z'],
    [51.4314, 0.6732, 3017, '2026-03-23T07:51:00Z'],
    [51.4405, 0.4847, 1392, '2026-03-23T07:52:30Z'],
    [51.4464, 0.2988, 592, '2026-03-23T07:54:00Z'],
    [51.4539, 0.1106, 252, '2026-03-23T07:55:30Z'],
    [51.4593, -0.0782, 109, '2026-03-23T07:57:00Z'],
    [51.463, -0.2663, 56, '2026-03-23T07:58:30Z'],
    [51.4692, -0.4552, 23, '2026-03-23T08:00:00Z'],
    [51.47, -0.4543, 25, '2026-03-23T08:00:00Z'],
  ]),

  'sample-aircraft-3': trackWithAltitude([
    [50.0352, 8.5704, 115, '2026-03-23T06:30:00Z'],
    [49.9298, 8.9255, 151, '2026-03-23T06:32:30Z'],
    [49.8265, 9.2784, 404, '2026-03-23T06:35:00Z'],
    [49.7202, 9.6297, 1740, '2026-03-23T06:37:30Z'],
    [49.6132, 9.9791, 5739, '2026-03-23T06:40:00Z'],
    [49.5063, 10.3277, 9398, '2026-03-23T06:42:30Z'],
    [49.3953, 10.6742, 10462, '2026-03-23T06:45:00Z'],
    [49.2877, 11.0199, 10663, '2026-03-23T06:47:30Z'],
    [49.1772, 11.3662, 10699, '2026-03-23T06:50:00Z'],
    [49.0635, 11.7046, 10695, '2026-03-23T06:52:30Z'],
    [48.9494, 12.0453, 10708, '2026-03-23T06:55:00Z'],
    [48.8366, 12.3876, 10699, '2026-03-23T06:57:30Z'],
    [48.7229, 12.7237, 10696, '2026-03-23T07:00:00Z'],
    [48.6088, 13.0596, 10706, '2026-03-23T07:02:30Z'],
    [48.4915, 13.3937, 10710, '2026-03-23T07:05:00Z'],
    [48.3717, 13.7273, 10693, '2026-03-23T07:07:30Z'],
    [48.2533, 14.0601, 10705, '2026-03-23T07:10:00Z'],
    [48.1346, 14.3893, 10696, '2026-03-23T07:12:30Z'],
    [48.0148, 14.7172, 10699, '2026-03-23T07:15:00Z'],
    [47.8913, 15.0437, 10697, '2026-03-23T07:17:30Z'],
    [47.7702, 15.3689, 10702, '2026-03-23T07:20:00Z'],
    [47.6473, 15.6941, 10694, '2026-03-23T07:22:30Z'],
    [47.5202, 16.0151, 10695, '2026-03-23T07:25:00Z'],
    [47.397, 16.3368, 10704, '2026-03-23T07:27:30Z'],
    [47.2711, 16.6546, 10701, '2026-03-23T07:30:00Z'],
    [47.1431, 16.9753, 10692, '2026-03-23T07:32:30Z'],
    [47.0152, 17.2878, 10698, '2026-03-23T07:35:00Z'],
    [46.8888, 17.6035, 10699, '2026-03-23T07:37:30Z'],
    [46.7595, 17.917, 10696, '2026-03-23T07:40:00Z'],
    [46.6276, 18.2284, 10706, '2026-03-23T07:42:30Z'],
    [46.4963, 18.5402, 10699, '2026-03-23T07:45:00Z'],
    [46.3659, 18.8455, 10704, '2026-03-23T07:47:30Z'],
    [46.231, 19.1568, 10698, '2026-03-23T07:50:00Z'],
    [46.0981, 19.4619, 10698, '2026-03-23T07:52:30Z'],
    [45.964, 19.7633, 10706, '2026-03-23T07:55:00Z'],
    [45.827, 20.0665, 10702, '2026-03-23T07:57:30Z'],
    [45.6907, 20.3686, 10707, '2026-03-23T08:00:00Z'],
    [45.5563, 20.6695, 10709, '2026-03-23T08:02:30Z'],
    [45.4169, 20.9669, 10693, '2026-03-23T08:05:00Z'],
    [45.2794, 21.2615, 10704, '2026-03-23T08:07:30Z'],
    [45.1403, 21.5582, 10697, '2026-03-23T08:10:00Z'],
    [44.9996, 21.8485, 10703, '2026-03-23T08:12:30Z'],
    [44.8592, 22.1454, 10695, '2026-03-23T08:15:00Z'],
    [44.7155, 22.4346, 10691, '2026-03-23T08:17:30Z'],
    [44.5773, 22.7239, 10699, '2026-03-23T08:20:00Z'],
    [44.4332, 23.0146, 10707, '2026-03-23T08:22:30Z'],
    [44.2898, 23.3, 10703, '2026-03-23T08:25:00Z'],
    [44.1459, 23.5862, 10701, '2026-03-23T08:27:30Z'],
    [43.999, 23.8695, 10706, '2026-03-23T08:30:00Z'],
    [43.8521, 24.1546, 10703, '2026-03-23T08:32:30Z'],
    [43.7089, 24.4351, 10700, '2026-03-23T08:35:00Z'],
    [43.5591, 24.7137, 10704, '2026-03-23T08:37:30Z'],
    [43.4104, 24.9954, 10698, '2026-03-23T08:40:00Z'],
    [43.2645, 25.2703, 10703, '2026-03-23T08:42:30Z'],
    [43.1158, 25.5461, 10709, '2026-03-23T08:45:00Z'],
    [42.9628, 25.8193, 10700, '2026-03-23T08:47:30Z'],
    [42.8144, 26.0919, 10703, '2026-03-23T08:50:00Z'],
    [42.6634, 26.3649, 10698, '2026-03-23T08:52:30Z'],
    [42.5112, 26.6359, 10614, '2026-03-23T08:55:00Z'],
    [42.3603, 26.9028, 10333, '2026-03-23T08:57:30Z'],
    [42.2071, 27.1712, 9277, '2026-03-23T09:00:00Z'],
    [42.053, 27.4381, 6411, '2026-03-23T09:02:30Z'],
    [41.8981, 27.7031, 2752, '2026-03-23T09:05:00Z'],
    [41.7431, 27.9674, 821, '2026-03-23T09:07:30Z'],
    [41.5876, 28.2294, 236, '2026-03-23T09:10:00Z'],
    [41.432, 28.491, 90, '2026-03-23T09:12:30Z'],
    [41.2764, 28.7508, 58, '2026-03-23T09:15:00Z'],
    [41.2753, 28.7519, 49, '2026-03-23T09:15:00Z'],
  ]),

  'sample-aircraft-4': trackWithAltitude([
    [49.0094, 2.546, 116, '2026-03-23T07:00:00Z'],
    [48.9202, 3.2713, 147, '2026-03-23T07:04:00Z'],
    [48.8252, 3.9908, 547, '2026-03-23T07:08:00Z'],
    [48.7258, 4.7097, 4209, '2026-03-23T07:12:00Z'],
    [48.6245, 5.4257, 10521, '2026-03-23T07:16:00Z'],
    [48.5155, 6.136, 11800, '2026-03-23T07:20:00Z'],
    [48.4031, 6.8468, 11897, '2026-03-23T07:24:00Z'],
    [48.2878, 7.5502, 11897, '2026-03-23T07:28:00Z'],
    [48.1682, 8.2529, 11903, '2026-03-23T07:32:00Z'],
    [48.0426, 8.9511, 11901, '2026-03-23T07:36:00Z'],
    [47.9145, 9.6479, 11898, '2026-03-23T07:40:00Z'],
    [47.7788, 10.3388, 11890, '2026-03-23T07:44:00Z'],
    [47.6438, 11.0272, 11907, '2026-03-23T07:48:00Z'],
    [47.5002, 11.7124, 11899, '2026-03-23T07:52:00Z'],
    [47.3546, 12.3949, 11907, '2026-03-23T07:56:00Z'],
    [47.2048, 13.0727, 11900, '2026-03-23T08:00:00Z'],
    [47.0519, 13.7458, 11897, '2026-03-23T08:04:00Z'],
    [46.8936, 14.4146, 11894, '2026-03-23T08:08:00Z'],
    [46.7338, 15.0793, 11903, '2026-03-23T08:12:00Z'],
    [46.568, 15.7429, 11907, '2026-03-23T08:16:00Z'],
    [46.3989, 16.3991, 11901, '2026-03-23T08:20:00Z'],
    [46.2272, 17.052, 11907, '2026-03-23T08:24:00Z'],
    [46.0511, 17.7022, 11899, '2026-03-23T08:28:00Z'],
    [45.8696, 18.3472, 11903, '2026-03-23T08:32:00Z'],
    [45.6861, 18.9884, 11898, '2026-03-23T08:36:00Z'],
    [45.4983, 19.6234, 11906, '2026-03-23T08:40:00Z'],
    [45.3075, 20.2548, 11898, '2026-03-23T08:44:00Z'],
    [45.1135, 20.8842, 11905, '2026-03-23T08:48:00Z'],
    [44.9167, 21.5095, 11915, '2026-03-23T08:52:00Z'],
    [44.7148, 22.1287, 11896, '2026-03-23T08:56:00Z'],
    [44.5102, 22.7434, 11897, '2026-03-23T09:00:00Z'],
    [44.3026, 23.3548, 11894, '2026-03-23T09:04:00Z'],
    [44.0933, 23.9601, 11909, '2026-03-23T09:08:00Z'],
    [43.8784, 24.5619, 11908, '2026-03-23T09:12:00Z'],
    [43.6612, 25.1604, 11903, '2026-03-23T09:16:00Z'],
    [43.4398, 25.7532, 11902, '2026-03-23T09:20:00Z'],
    [43.2171, 26.3424, 11899, '2026-03-23T09:24:00Z'],
    [42.9896, 26.9262, 11892, '2026-03-23T09:28:00Z'],
    [42.7614, 27.5085, 11905, '2026-03-23T09:32:00Z'],
    [42.5302, 28.0827, 11899, '2026-03-23T09:36:00Z'],
    [42.2942, 28.6562, 11906, '2026-03-23T09:40:00Z'],
    [42.0594, 29.2233, 11898, '2026-03-23T09:44:00Z'],
    [41.8159, 29.7887, 11904, '2026-03-23T09:48:00Z'],
    [41.5718, 30.3449, 11902, '2026-03-23T09:52:00Z'],
    [41.3261, 30.8997, 11897, '2026-03-23T09:56:00Z'],
    [41.078, 31.4496, 11900, '2026-03-23T10:00:00Z'],
    [40.8248, 31.9971, 11899, '2026-03-23T10:04:00Z'],
    [40.5747, 32.5383, 11900, '2026-03-23T10:08:00Z'],
    [40.3168, 33.077, 11905, '2026-03-23T10:12:00Z'],
    [40.0575, 33.611, 11896, '2026-03-23T10:16:00Z'],
    [39.7969, 34.143, 11908, '2026-03-23T10:20:00Z'],
    [39.5323, 34.6692, 11907, '2026-03-23T10:24:00Z'],
    [39.2663, 35.1897, 11899, '2026-03-23T10:28:00Z'],
    [38.9979, 35.7086, 11888, '2026-03-23T10:32:00Z'],
    [38.7278, 36.2229, 11895, '2026-03-23T10:36:00Z'],
    [38.4544, 36.7318, 11901, '2026-03-23T10:40:00Z'],
    [38.1802, 37.239, 11904, '2026-03-23T10:44:00Z'],
    [37.9035, 37.7401, 11900, '2026-03-23T10:48:00Z'],
    [37.6237, 38.2402, 11896, '2026-03-23T10:52:00Z'],
    [37.3426, 38.7335, 11902, '2026-03-23T10:56:00Z'],
    [37.0586, 39.2271, 11900, '2026-03-23T11:00:00Z'],
    [36.7724, 39.7145, 11900, '2026-03-23T11:04:00Z'],
    [36.4839, 40.1972, 11901, '2026-03-23T11:08:00Z'],
    [36.1951, 40.6785, 11897, '2026-03-23T11:12:00Z'],
    [35.9052, 41.157, 11895, '2026-03-23T11:16:00Z'],
    [35.6119, 41.629, 11897, '2026-03-23T11:20:00Z'],
    [35.3168, 42.1011, 11901, '2026-03-23T11:24:00Z'],
    [35.0201, 42.5661, 11900, '2026-03-23T11:28:00Z'],
    [34.7192, 43.029, 11896, '2026-03-23T11:32:00Z'],
    [34.4181, 43.4871, 11896, '2026-03-23T11:36:00Z'],
    [34.1199, 43.9438, 11909, '2026-03-23T11:40:00Z'],
    [33.8161, 44.3974, 11898, '2026-03-23T11:44:00Z'],
    [33.511, 44.8481, 11898, '2026-03-23T11:48:00Z'],
    [33.2022, 45.2948, 11906, '2026-03-23T11:52:00Z'],
    [32.895, 45.7401, 11901, '2026-03-23T11:56:00Z'],
    [32.5839, 46.1798, 11899, '2026-03-23T12:00:00Z'],
    [32.2738, 46.615, 11899, '2026-03-23T12:04:00Z'],
    [31.9601, 47.0517, 11900, '2026-03-23T12:08:00Z'],
    [31.646, 47.4808, 11897, '2026-03-23T12:12:00Z'],
    [31.3286, 47.911, 11894, '2026-03-23T12:16:00Z'],
    [31.0122, 48.3362, 11895, '2026-03-23T12:20:00Z'],
    [30.6914, 48.7607, 11897, '2026-03-23T12:24:00Z'],
    [30.3717, 49.1802, 11898, '2026-03-23T12:28:00Z'],
    [30.0495, 49.5958, 11908, '2026-03-23T12:32:00Z'],
    [29.7264, 50.0114, 11903, '2026-03-23T12:36:00Z'],
    [29.404, 50.4249, 11902, '2026-03-23T12:40:00Z'],
    [29.0802, 50.834, 11895, '2026-03-23T12:44:00Z'],
    [28.7511, 51.239, 11895, '2026-03-23T12:48:00Z'],
    [28.4225, 51.6444, 11911, '2026-03-23T12:52:00Z'],
    [28.0949, 52.0462, 11902, '2026-03-23T12:56:00Z'],
    [27.7655, 52.4459, 11900, '2026-03-23T13:00:00Z'],
    [27.4358, 52.8421, 11898, '2026-03-23T13:04:00Z'],
    [27.1002, 53.2363, 11751, '2026-03-23T13:08:00Z'],
    [26.7666, 53.6286, 10503, '2026-03-23T13:12:00Z'],
    [26.4316, 54.0188, 5210, '2026-03-23T13:16:00Z'],
    [26.0959, 54.4053, 898, '2026-03-23T13:20:00Z'],
    [25.7607, 54.7908, 109, '2026-03-23T13:24:00Z'],
    [25.4225, 55.1733, 22, '2026-03-23T13:28:00Z'],
    [25.2532, 55.3657, 19, '2026-03-23T13:30:00Z'],
  ]),

  'sample-aircraft-5': trackWithAltitude([
    [52.3075, 4.7632, -4, '2026-03-23T08:00:00Z'],
    [52.4606, 4.0914, -5, '2026-03-23T08:04:00Z'],
    [52.6098, 3.4127, 261, '2026-03-23T08:08:00Z'],
    [52.7536, 2.7291, 3170, '2026-03-23T08:12:00Z'],
    [52.8918, 2.0436, 9980, '2026-03-23T08:16:00Z'],
    [53.0298, 1.349, 11484, '2026-03-23T08:20:00Z'],
    [53.1626, 0.6564, 11600, '2026-03-23T08:24:00Z'],
    [53.2905, -0.0438, 11604, '2026-03-23T08:28:00Z'],
    [53.4145, -0.7481, 11605, '2026-03-23T08:32:00Z'],
    [53.534, -1.4559, 11600, '2026-03-23T08:36:00Z'],
    [53.6496, -2.1669, 11589, '2026-03-23T08:40:00Z'],
    [53.7609, -2.8842, 11602, '2026-03-23T08:44:00Z'],
    [53.869, -3.6048, 11605, '2026-03-23T08:48:00Z'],
    [53.9697, -4.3297, 11596, '2026-03-23T08:52:00Z'],
    [54.0694, -5.0547, 11613, '2026-03-23T08:56:00Z'],
    [54.1631, -5.7855, 11591, '2026-03-23T09:00:00Z'],
    [54.2511, -6.5185, 11610, '2026-03-23T09:04:00Z'],
    [54.338, -7.2534, 11599, '2026-03-23T09:08:00Z'],
    [54.4195, -7.9945, 11606, '2026-03-23T09:12:00Z'],
    [54.4961, -8.7368, 11601, '2026-03-23T09:16:00Z'],
    [54.5667, -9.4821, 11602, '2026-03-23T09:20:00Z'],
    [54.6337, -10.2299, 11594, '2026-03-23T09:24:00Z'],
    [54.6951, -10.9799, 11594, '2026-03-23T09:28:00Z'],
    [54.7536, -11.7309, 11602, '2026-03-23T09:32:00Z'],
    [54.8059, -12.4863, 11600, '2026-03-23T09:36:00Z'],
    [54.8555, -13.2425, 11601, '2026-03-23T09:40:00Z'],
    [54.8987, -14.0007, 11601, '2026-03-23T09:44:00Z'],
    [54.9391, -14.7605, 11600, '2026-03-23T09:48:00Z'],
    [54.9718, -15.519, 11601, '2026-03-23T09:52:00Z'],
    [55.0018, -16.2834, 11607, '2026-03-23T09:56:00Z'],
    [55.0277, -17.0438, 11606, '2026-03-23T10:00:00Z'],
    [55.0447, -17.8089, 11595, '2026-03-23T10:04:00Z'],
    [55.0605, -18.5755, 11597, '2026-03-23T10:08:00Z'],
    [55.0724, -19.3391, 11600, '2026-03-23T10:12:00Z'],
    [55.0749, -20.1027, 11595, '2026-03-23T10:16:00Z'],
    [55.0775, -20.8711, 11598, '2026-03-23T10:20:00Z'],
    [55.0724, -21.6341, 11594, '2026-03-23T10:24:00Z'],
    [55.0637, -22.4005, 11605, '2026-03-23T10:28:00Z'],
    [55.0508, -23.1645, 11597, '2026-03-23T10:32:00Z'],
    [55.031, -23.9302, 11598, '2026-03-23T10:36:00Z'],
    [55.0064, -24.6933, 11596, '2026-03-23T10:40:00Z'],
    [54.9823, -25.4554, 11593, '2026-03-23T10:44:00Z'],
    [54.9482, -26.2152, 11606, '2026-03-23T10:48:00Z'],
    [54.9101, -26.9777, 11600, '2026-03-23T10:52:00Z'],
    [54.8687, -27.7342, 11597, '2026-03-23T10:56:00Z'],
    [54.8208, -28.4919, 11602, '2026-03-23T11:00:00Z'],
    [54.7689, -29.2458, 11604, '2026-03-23T11:04:00Z'],
    [54.7144, -29.9976, 11600, '2026-03-23T11:08:00Z'],
    [54.6538, -30.7485, 11598, '2026-03-23T11:12:00Z'],
    [54.5868, -31.4985, 11603, '2026-03-23T11:16:00Z'],
    [54.5166, -32.2453, 11602, '2026-03-23T11:20:00Z'],
    [54.4409, -32.987, 11595, '2026-03-23T11:24:00Z'],
    [54.362, -33.7265, 11603, '2026-03-23T11:28:00Z'],
    [54.2795, -34.4641, 11601, '2026-03-23T11:32:00Z'],
    [54.1895, -35.2006, 11600, '2026-03-23T11:36:00Z'],
    [54.0962, -35.9293, 11595, '2026-03-23T11:40:00Z'],
    [54.0002, -36.6577, 11595, '2026-03-23T11:44:00Z'],
    [53.8993, -37.3831, 11596, '2026-03-23T11:48:00Z'],
    [53.7918, -38.1038, 11587, '2026-03-23T11:52:00Z'],
    [53.6815, -38.8206, 11596, '2026-03-23T11:56:00Z'],
    [53.5684, -39.5339, 11599, '2026-03-23T12:00:00Z'],
    [53.449, -40.2434, 11598, '2026-03-23T12:04:00Z'],
    [53.3267, -40.9473, 11595, '2026-03-23T12:08:00Z'],
    [53.1986, -41.6502, 11604, '2026-03-23T12:12:00Z'],
    [53.0688, -42.3461, 11607, '2026-03-23T12:16:00Z'],
    [52.9332, -43.0395, 11604, '2026-03-23T12:20:00Z'],
    [52.7912, -43.7259, 11596, '2026-03-23T12:24:00Z'],
    [52.6497, -44.41, 11589, '2026-03-23T12:28:00Z'],
    [52.5032, -45.0891, 11606, '2026-03-23T12:32:00Z'],
    [52.3524, -45.7648, 11596, '2026-03-23T12:36:00Z'],
    [52.1974, -46.4349, 11595, '2026-03-23T12:40:00Z'],
    [52.04, -47.1023, 11592, '2026-03-23T12:44:00Z'],
    [51.8784, -47.7628, 11595, '2026-03-23T12:48:00Z'],
    [51.7111, -48.4159, 11592, '2026-03-23T12:52:00Z'],
    [51.5427, -49.0681, 11603, '2026-03-23T12:56:00Z'],
    [51.3701, -49.7161, 11602, '2026-03-23T13:00:00Z'],
    [51.1917, -50.3552, 11602, '2026-03-23T13:04:00Z'],
    [51.0123, -50.9912, 11602, '2026-03-23T13:08:00Z'],
    [50.8297, -51.622, 11590, '2026-03-23T13:12:00Z'],
    [50.6394, -52.25, 11596, '2026-03-23T13:16:00Z'],
    [50.4507, -52.8686, 11602, '2026-03-23T13:20:00Z'],
    [50.2583, -53.4871, 11602, '2026-03-23T13:24:00Z'],
    [50.0611, -54.0984, 11607, '2026-03-23T13:28:00Z'],
    [49.8626, -54.7034, 11595, '2026-03-23T13:32:00Z'],
    [49.6571, -55.3053, 11603, '2026-03-23T13:36:00Z'],
    [49.453, -55.9009, 11605, '2026-03-23T13:40:00Z'],
    [49.2434, -56.493, 11609, '2026-03-23T13:44:00Z'],
    [49.031, -57.0809, 11606, '2026-03-23T13:48:00Z'],
    [48.8172, -57.659, 11586, '2026-03-23T13:52:00Z'],
    [48.5994, -58.2362, 11601, '2026-03-23T13:56:00Z'],
    [48.3754, -58.8067, 11605, '2026-03-23T14:00:00Z'],
    [48.1534, -59.3734, 11601, '2026-03-23T14:04:00Z'],
    [47.9268, -59.9355, 11596, '2026-03-23T14:08:00Z'],
    [47.6984, -60.4896, 11609, '2026-03-23T14:12:00Z'],
    [47.4647, -61.043, 11600, '2026-03-23T14:16:00Z'],
    [47.2336, -61.5881, 11592, '2026-03-23T14:20:00Z'],
    [46.9937, -62.1288, 11594, '2026-03-23T14:24:00Z'],
    [46.7561, -62.6681, 11597, '2026-03-23T14:28:00Z'],
    [46.5125, -63.1972, 11603, '2026-03-23T14:32:00Z'],
    [46.2682, -63.7264, 11593, '2026-03-23T14:36:00Z'],
    [46.0225, -64.248, 11607, '2026-03-23T14:40:00Z'],
    [45.7731, -64.7677, 11599, '2026-03-23T14:44:00Z'],
    [45.5212, -65.2823, 11603, '2026-03-23T14:48:00Z'],
    [45.2698, -65.7891, 11595, '2026-03-23T14:52:00Z'],
    [45.0107, -66.2922, 11604, '2026-03-23T14:56:00Z'],
    [44.753, -66.7924, 11597, '2026-03-23T15:00:00Z'],
    [44.4941, -67.2888, 11598, '2026-03-23T15:04:00Z'],
    [44.2311, -67.7776, 11597, '2026-03-23T15:08:00Z'],
    [43.9652, -68.2662, 11595, '2026-03-23T15:12:00Z'],
    [43.6996, -68.7478, 11602, '2026-03-23T15:16:00Z'],
    [43.4292, -69.2239, 11593, '2026-03-23T15:20:00Z'],
    [43.1605, -69.6999, 11597, '2026-03-23T15:24:00Z'],
    [42.8862, -70.1669, 11596, '2026-03-23T15:28:00Z'],
    [42.6135, -70.6334, 11606, '2026-03-23T15:32:00Z'],
    [42.3358, -71.0937, 11540, '2026-03-23T15:36:00Z'],
    [42.0572, -71.5524, 11079, '2026-03-23T15:40:00Z'],
    [41.7786, -72.0052, 7630, '2026-03-23T15:44:00Z'],
    [41.4974, -72.4545, 1770, '2026-03-23T15:48:00Z'],
    [41.2132, -72.8992, 194, '2026-03-23T15:52:00Z'],
    [40.9285, -73.3398, 11, '2026-03-23T15:56:00Z'],
    [40.6436, -73.7791, 2, '2026-03-23T16:00:00Z'],
    [40.6413, -73.7781, 4, '2026-03-23T16:00:00Z'],
  ]),

  'sample-aircraft-6': trackWithAltitude([
    [41.8002, 12.238, 1, '2026-03-23T09:00:00Z'],
    [41.759, 12.3927, 41, '2026-03-23T09:01:30Z'],
    [41.7193, 12.5501, 123, '2026-03-23T09:03:00Z'],
    [41.679, 12.7005, 362, '2026-03-23T09:04:30Z'],
    [41.6367, 12.8551, 1093, '2026-03-23T09:06:00Z'],
    [41.5963, 13.0108, 2939, '2026-03-23T09:07:30Z'],
    [41.5527, 13.1623, 5860, '2026-03-23T09:09:00Z'],
    [41.512, 13.3148, 8321, '2026-03-23T09:10:30Z'],
    [41.4678, 13.4693, 9495, '2026-03-23T09:12:00Z'],
    [41.4272, 13.6224, 9917, '2026-03-23T09:13:30Z'],
    [41.3837, 13.7735, 10045, '2026-03-23T09:15:00Z'],
    [41.3397, 13.9266, 10102, '2026-03-23T09:16:30Z'],
    [41.297, 14.0772, 10093, '2026-03-23T09:18:00Z'],
    [41.2544, 14.2321, 10102, '2026-03-23T09:19:30Z'],
    [41.2105, 14.382, 10108, '2026-03-23T09:21:00Z'],
    [41.1673, 14.5337, 10096, '2026-03-23T09:22:30Z'],
    [41.1257, 14.6851, 10099, '2026-03-23T09:24:00Z'],
    [41.0811, 14.837, 10104, '2026-03-23T09:25:30Z'],
    [41.0365, 14.9902, 10107, '2026-03-23T09:27:00Z'],
    [40.9906, 15.1392, 10099, '2026-03-23T09:28:30Z'],
    [40.9478, 15.2884, 10102, '2026-03-23T09:30:00Z'],
    [40.9019, 15.4389, 10093, '2026-03-23T09:31:30Z'],
    [40.857, 15.5883, 10101, '2026-03-23T09:33:00Z'],
    [40.8124, 15.7387, 10104, '2026-03-23T09:34:30Z'],
    [40.7679, 15.8885, 10095, '2026-03-23T09:36:00Z'],
    [40.7204, 16.0379, 10103, '2026-03-23T09:37:30Z'],
    [40.6772, 16.1889, 10098, '2026-03-23T09:39:00Z'],
    [40.6309, 16.3363, 10097, '2026-03-23T09:40:30Z'],
    [40.5831, 16.4872, 10099, '2026-03-23T09:42:00Z'],
    [40.5383, 16.6346, 10107, '2026-03-23T09:43:30Z'],
    [40.4902, 16.781, 10102, '2026-03-23T09:45:00Z'],
    [40.4447, 16.9313, 10090, '2026-03-23T09:46:30Z'],
    [40.3979, 17.0812, 10097, '2026-03-23T09:48:00Z'],
    [40.3504, 17.2281, 10109, '2026-03-23T09:49:30Z'],
    [40.3026, 17.3761, 10102, '2026-03-23T09:51:00Z'],
    [40.2548, 17.5253, 10097, '2026-03-23T09:52:30Z'],
    [40.2062, 17.6708, 10098, '2026-03-23T09:54:00Z'],
    [40.1583, 17.818, 10093, '2026-03-23T09:55:30Z'],
    [40.1102, 17.9669, 10101, '2026-03-23T09:57:00Z'],
    [40.0644, 18.112, 10098, '2026-03-23T09:58:30Z'],
    [40.0163, 18.2591, 10099, '2026-03-23T10:00:00Z'],
    [39.9666, 18.4023, 10099, '2026-03-23T10:01:30Z'],
    [39.9182, 18.5502, 10095, '2026-03-23T10:03:00Z'],
    [39.8679, 18.6954, 10099, '2026-03-23T10:04:30Z'],
    [39.82, 18.8397, 10092, '2026-03-23T10:06:00Z'],
    [39.7718, 18.9886, 10094, '2026-03-23T10:07:30Z'],
    [39.7212, 19.1319, 10094, '2026-03-23T10:09:00Z'],
    [39.6721, 19.2752, 10107, '2026-03-23T10:10:30Z'],
    [39.621, 19.4192, 10105, '2026-03-23T10:12:00Z'],
    [39.5735, 19.5664, 10090, '2026-03-23T10:13:30Z'],
    [39.5223, 19.7098, 10100, '2026-03-23T10:15:00Z'],
    [39.4734, 19.8535, 10104, '2026-03-23T10:16:30Z'],
    [39.4209, 19.9985, 10104, '2026-03-23T10:18:00Z'],
    [39.3712, 20.145, 10093, '2026-03-23T10:19:30Z'],
    [39.3199, 20.2869, 10106, '2026-03-23T10:21:00Z'],
    [39.2675, 20.4289, 10093, '2026-03-23T10:22:30Z'],
    [39.2183, 20.5711, 10093, '2026-03-23T10:24:00Z'],
    [39.1656, 20.7143, 10098, '2026-03-23T10:25:30Z'],
    [39.1152, 20.8569, 10103, '2026-03-23T10:27:00Z'],
    [39.0621, 21.0005, 10097, '2026-03-23T10:28:30Z'],
    [39.0112, 21.1429, 10106, '2026-03-23T10:30:00Z'],
    [38.957, 21.2837, 10104, '2026-03-23T10:31:30Z'],
    [38.9059, 21.4259, 10107, '2026-03-23T10:33:00Z'],
    [38.8535, 21.5664, 10103, '2026-03-23T10:34:30Z'],
    [38.8004, 21.7102, 10102, '2026-03-23T10:36:00Z'],
    [38.7482, 21.8513, 10104, '2026-03-23T10:37:30Z'],
    [38.6968, 21.9921, 10104, '2026-03-23T10:39:00Z'],
    [38.6406, 22.1338, 10104, '2026-03-23T10:40:30Z'],
    [38.5891, 22.2754, 10097, '2026-03-23T10:42:00Z'],
    [38.5353, 22.4142, 10037, '2026-03-23T10:43:30Z'],
    [38.4824, 22.5542, 9889, '2026-03-23T10:45:00Z'],
    [38.4286, 22.6924, 9523, '2026-03-23T10:46:30Z'],
    [38.3733, 22.8337, 8568, '2026-03-23T10:48:00Z'],
    [38.321, 22.9744, 6653, '2026-03-23T10:49:30Z'],
    [38.2658, 23.1132, 4038, '2026-03-23T10:51:00Z'],
    [38.2106, 23.253, 1918, '2026-03-23T10:52:30Z'],
    [38.1572, 23.3902, 802, '2026-03-23T10:54:00Z'],
    [38.1008, 23.5276, 346, '2026-03-23T10:55:30Z'],
    [38.0465, 23.667, 184, '2026-03-23T10:57:00Z'],
    [37.9901, 23.8062, 124, '2026-03-23T10:58:30Z'],
    [37.9347, 23.9457, 101, '2026-03-23T11:00:00Z'],
    [37.9364, 23.9445, 94, '2026-03-23T11:00:00Z'],
  ]),

  'sample-aircraft-7': trackWithAltitude([
    [48.1098, 16.5701, 190, '2026-03-23T10:00:00Z'],
    [48.2681, 16.8576, 242, '2026-03-23T10:02:30Z'],
    [48.427, 17.1494, 681, '2026-03-23T10:05:00Z'],
    [48.5833, 17.4422, 3327, '2026-03-23T10:07:30Z'],
    [48.7381, 17.7367, 7837, '2026-03-23T10:10:00Z'],
    [48.8935, 18.0348, 9244, '2026-03-23T10:12:30Z'],
    [49.0475, 18.3305, 9450, '2026-03-23T10:15:00Z'],
    [49.2, 18.6276, 9451, '2026-03-23T10:17:30Z'],
    [49.3553, 18.9322, 9451, '2026-03-23T10:20:00Z'],
    [49.507, 19.2367, 9449, '2026-03-23T10:22:30Z'],
    [49.6569, 19.5396, 9454, '2026-03-23T10:25:00Z'],
    [49.8084, 19.8479, 9440, '2026-03-23T10:27:30Z'],
    [49.9581, 20.1572, 9450, '2026-03-23T10:30:00Z'],
    [50.1066, 20.4691, 9450, '2026-03-23T10:32:30Z'],
    [50.2517, 20.7819, 9454, '2026-03-23T10:35:00Z'],
    [50.4015, 21.0971, 9450, '2026-03-23T10:37:30Z'],
    [50.5481, 21.4142, 9452, '2026-03-23T10:40:00Z'],
    [50.6939, 21.7341, 9453, '2026-03-23T10:42:30Z'],
    [50.8391, 22.0555, 9453, '2026-03-23T10:45:00Z'],
    [50.9821, 22.3775, 9449, '2026-03-23T10:47:30Z'],
    [51.1247, 22.7022, 9458, '2026-03-23T10:50:00Z'],
    [51.2659, 23.0295, 9442, '2026-03-23T10:52:30Z'],
    [51.407, 23.3573, 9455, '2026-03-23T10:55:00Z'],
    [51.5464, 23.6876, 9450, '2026-03-23T10:57:30Z'],
    [51.6841, 24.0215, 9453, '2026-03-23T11:00:00Z'],
    [51.8252, 24.3592, 9454, '2026-03-23T11:02:30Z'],
    [51.9609, 24.6956, 9458, '2026-03-23T11:05:00Z'],
    [52.0941, 25.0336, 9448, '2026-03-23T11:07:30Z'],
    [52.231, 25.3755, 9452, '2026-03-23T11:10:00Z'],
    [52.3656, 25.7166, 9445, '2026-03-23T11:12:30Z'],
    [52.4973, 26.0626, 9461, '2026-03-23T11:15:00Z'],
    [52.6311, 26.4109, 9445, '2026-03-23T11:17:30Z'],
    [52.762, 26.7617, 9451, '2026-03-23T11:20:00Z'],
    [52.8924, 27.1108, 9458, '2026-03-23T11:22:30Z'],
    [53.0218, 27.4663, 9441, '2026-03-23T11:25:00Z'],
    [53.1496, 27.8197, 9448, '2026-03-23T11:27:30Z'],
    [53.2771, 28.1795, 9452, '2026-03-23T11:30:00Z'],
    [53.4036, 28.5416, 9452, '2026-03-23T11:32:30Z'],
    [53.5244, 28.9041, 9447, '2026-03-23T11:35:00Z'],
    [53.6495, 29.2682, 9453, '2026-03-23T11:37:30Z'],
    [53.7741, 29.6329, 9442, '2026-03-23T11:40:00Z'],
    [53.8958, 30.0025, 9450, '2026-03-23T11:42:30Z'],
    [54.0166, 30.3743, 9439, '2026-03-23T11:45:00Z'],
    [54.1324, 30.7487, 9451, '2026-03-23T11:47:30Z'],
    [54.2521, 31.1217, 9449, '2026-03-23T11:50:00Z'],
    [54.3685, 31.5005, 9457, '2026-03-23T11:52:30Z'],
    [54.4851, 31.8794, 9451, '2026-03-23T11:55:00Z'],
    [54.6002, 32.2623, 9449, '2026-03-23T11:57:30Z'],
    [54.7118, 32.6431, 9450, '2026-03-23T12:00:00Z'],
    [54.8241, 33.0336, 9457, '2026-03-23T12:02:30Z'],
    [54.9372, 33.4202, 9445, '2026-03-23T12:05:00Z'],
    [55.044, 33.8088, 9450, '2026-03-23T12:07:30Z'],
    [55.1545, 34.2021, 9452, '2026-03-23T12:10:00Z'],
    [55.2601, 34.5952, 9424, '2026-03-23T12:12:30Z'],
    [55.3653, 34.992, 9242, '2026-03-23T12:15:00Z'],
    [55.469, 35.3879, 8351, '2026-03-23T12:17:30Z'],
    [55.5744, 35.7907, 5248, '2026-03-23T12:20:00Z'],
    [55.6745, 36.1945, 1696, '2026-03-23T12:22:30Z'],
    [55.7768, 36.5988, 482, '2026-03-23T12:25:00Z'],
    [55.8759, 37.0051, 239, '2026-03-23T12:27:30Z'],
    [55.9736, 37.4153, 189, '2026-03-23T12:30:00Z'],
    [55.9726, 37.4146, 190, '2026-03-23T12:30:00Z'],
  ]),

  'sample-aircraft-8': trackWithAltitude([
    [40.4939, -3.5662, 611, '2026-03-23T11:00:00Z'],
    [40.46, -3.688, 642, '2026-03-23T11:01:30Z'],
    [40.4244, -3.8118, 759, '2026-03-23T11:03:00Z'],
    [40.3916, -3.9325, 1284, '2026-03-23T11:04:30Z'],
    [40.3584, -4.0553, 2855, '2026-03-23T11:06:00Z'],
    [40.3249, -4.1758, 4850, '2026-03-23T11:07:30Z'],
    [40.288, -4.2985, 5776, '2026-03-23T11:09:00Z'],
    [40.2546, -4.4194, 6038, '2026-03-23T11:10:30Z'],
    [40.2184, -4.5396, 6103, '2026-03-23T11:12:00Z'],
    [40.1851, -4.663, 6102, '2026-03-23T11:13:30Z'],
    [40.1492, -4.7831, 6090, '2026-03-23T11:15:00Z'],
    [40.1144, -4.9029, 6104, '2026-03-23T11:16:30Z'],
    [40.0793, -5.026, 6105, '2026-03-23T11:18:00Z'],
    [40.0426, -5.1462, 6092, '2026-03-23T11:19:30Z'],
    [40.0076, -5.2667, 6103, '2026-03-23T11:21:00Z'],
    [39.9722, -5.3867, 6110, '2026-03-23T11:22:30Z'],
    [39.9355, -5.5066, 6104, '2026-03-23T11:24:00Z'],
    [39.9013, -5.6267, 6103, '2026-03-23T11:25:30Z'],
    [39.8656, -5.7476, 6098, '2026-03-23T11:27:00Z'],
    [39.8281, -5.8676, 6095, '2026-03-23T11:28:30Z'],
    [39.7909, -5.9863, 6086, '2026-03-23T11:30:00Z'],
    [39.7557, -6.1058, 6102, '2026-03-23T11:31:30Z'],
    [39.7191, -6.2263, 6106, '2026-03-23T11:33:00Z'],
    [39.6829, -6.3464, 6101, '2026-03-23T11:34:30Z'],
    [39.648, -6.4656, 6089, '2026-03-23T11:36:00Z'],
    [39.6098, -6.5841, 6102, '2026-03-23T11:37:30Z'],
    [39.5741, -6.7051, 6099, '2026-03-23T11:39:00Z'],
    [39.5344, -6.8218, 6107, '2026-03-23T11:40:30Z'],
    [39.497, -6.9424, 6101, '2026-03-23T11:42:00Z'],
    [39.4606, -7.06, 6099, '2026-03-23T11:43:30Z'],
    [39.4238, -7.178, 6095, '2026-03-23T11:45:00Z'],
    [39.3849, -7.2958, 6100, '2026-03-23T11:46:30Z'],
    [39.3496, -7.4171, 6104, '2026-03-23T11:48:00Z'],
    [39.3115, -7.5329, 6092, '2026-03-23T11:49:30Z'],
    [39.2707, -7.6525, 6096, '2026-03-23T11:51:00Z'],
    [39.234, -7.7682, 6099, '2026-03-23T11:52:30Z'],
    [39.1961, -7.8863, 6090, '2026-03-23T11:54:00Z'],
    [39.1569, -8.0049, 6101, '2026-03-23T11:55:30Z'],
    [39.1208, -8.1231, 6060, '2026-03-23T11:57:00Z'],
    [39.0804, -8.239, 5962, '2026-03-23T11:58:30Z'],
    [39.0419, -8.3581, 5600, '2026-03-23T12:00:00Z'],
    [39.0031, -8.4737, 4536, '2026-03-23T12:01:30Z'],
    [38.9654, -8.5911, 2631, '2026-03-23T12:03:00Z'],
    [38.9259, -8.7073, 1061, '2026-03-23T12:04:30Z'],
    [38.8862, -8.8268, 384, '2026-03-23T12:06:00Z'],
    [38.8463, -8.9413, 183, '2026-03-23T12:07:30Z'],
    [38.8084, -9.0582, 132, '2026-03-23T12:09:00Z'],
    [38.7813, -9.1359, 114, '2026-03-23T12:10:00Z'],
  ]),

  'sample-aircraft-9': trackWithAltitude([
    [47.4647, 8.5498, 430, '2026-03-23T12:00:00Z'],
    [47.3805, 8.4475, 459, '2026-03-23T12:01:30Z'],
    [47.2944, 8.3433, 556, '2026-03-23T12:03:00Z'],
    [47.2083, 8.2416, 839, '2026-03-23T12:04:30Z'],
    [47.1236, 8.1402, 1659, '2026-03-23T12:06:00Z'],
    [47.0375, 8.0404, 3659, '2026-03-23T12:07:30Z'],
    [46.9519, 7.9395, 6716, '2026-03-23T12:09:00Z'],
    [46.8645, 7.8378, 9206, '2026-03-23T12:10:30Z'],
    [46.7783, 7.7366, 10382, '2026-03-23T12:12:00Z'],
    [46.6937, 7.6372, 10820, '2026-03-23T12:13:30Z'],
    [46.6069, 7.5363, 10943, '2026-03-23T12:15:00Z'],
    [46.521, 7.4368, 10997, '2026-03-23T12:16:30Z'],
    [46.4331, 7.3389, 11008, '2026-03-23T12:18:00Z'],
    [46.3484, 7.2404, 10997, '2026-03-23T12:19:30Z'],
    [46.2628, 7.1417, 11007, '2026-03-23T12:21:00Z'],
    [46.1745, 7.0443, 10988, '2026-03-23T12:22:30Z'],
    [46.0892, 6.9467, 11000, '2026-03-23T12:24:00Z'],
    [46.0023, 6.8476, 11000, '2026-03-23T12:25:30Z'],
    [45.9143, 6.7493, 11002, '2026-03-23T12:27:00Z'],
    [45.8271, 6.6539, 11002, '2026-03-23T12:28:30Z'],
    [45.7398, 6.5575, 10992, '2026-03-23T12:30:00Z'],
    [45.6546, 6.4609, 11003, '2026-03-23T12:31:30Z'],
    [45.5669, 6.366, 10995, '2026-03-23T12:33:00Z'],
    [45.4796, 6.2689, 10999, '2026-03-23T12:34:30Z'],
    [45.3929, 6.1743, 11009, '2026-03-23T12:36:00Z'],
    [45.3041, 6.0787, 10996, '2026-03-23T12:37:30Z'],
    [45.2156, 5.9844, 11005, '2026-03-23T12:39:00Z'],
    [45.1295, 5.8899, 11003, '2026-03-23T12:40:30Z'],
    [45.0426, 5.7961, 10999, '2026-03-23T12:42:00Z'],
    [44.9542, 5.7011, 11003, '2026-03-23T12:43:30Z'],
    [44.8673, 5.6081, 10996, '2026-03-23T12:45:00Z'],
    [44.7793, 5.5116, 10993, '2026-03-23T12:46:30Z'],
    [44.6917, 5.4224, 11001, '2026-03-23T12:48:00Z'],
    [44.6033, 5.328, 10997, '2026-03-23T12:49:30Z'],
    [44.5131, 5.2347, 10995, '2026-03-23T12:51:00Z'],
    [44.4265, 5.1456, 11002, '2026-03-23T12:52:30Z'],
    [44.3394, 5.0523, 10998, '2026-03-23T12:54:00Z'],
    [44.2495, 4.9599, 11005, '2026-03-23T12:55:30Z'],
    [44.162, 4.8688, 10997, '2026-03-23T12:57:00Z'],
    [44.0742, 4.7769, 11002, '2026-03-23T12:58:30Z'],
    [43.9843, 4.6852, 10998, '2026-03-23T13:00:00Z'],
    [43.8969, 4.5957, 10992, '2026-03-23T13:01:30Z'],
    [43.8079, 4.5044, 11010, '2026-03-23T13:03:00Z'],
    [43.7186, 4.4156, 11002, '2026-03-23T13:04:30Z'],
    [43.6305, 4.3247, 11011, '2026-03-23T13:06:00Z'],
    [43.5413, 4.2345, 11002, '2026-03-23T13:07:30Z'],
    [43.4513, 4.1454, 10999, '2026-03-23T13:09:00Z'],
    [43.3606, 4.0584, 11001, '2026-03-23T13:10:30Z'],
    [43.2735, 3.9674, 11005, '2026-03-23T13:12:00Z'],
    [43.1839, 3.8802, 11000, '2026-03-23T13:13:30Z'],
    [43.0955, 3.7911, 11001, '2026-03-23T13:15:00Z'],
    [43.005, 3.7034, 11008, '2026-03-23T13:16:30Z'],
    [42.9149, 3.6151, 11005, '2026-03-23T13:18:00Z'],
    [42.8265, 3.5294, 10996, '2026-03-23T13:19:30Z'],
    [42.7386, 3.4395, 11005, '2026-03-23T13:21:00Z'],
    [42.6478, 3.3542, 11000, '2026-03-23T13:22:30Z'],
    [42.5598, 3.2678, 11009, '2026-03-23T13:24:00Z'],
    [42.4684, 3.1802, 10988, '2026-03-23T13:25:30Z'],
    [42.3799, 3.0942, 10975, '2026-03-23T13:27:00Z'],
    [42.2895, 3.0078, 10926, '2026-03-23T13:28:30Z'],
    [42.1994, 2.922, 10775, '2026-03-23T13:30:00Z'],
    [42.1091, 2.8386, 10381, '2026-03-23T13:31:30Z'],
    [42.0187, 2.7531, 9375, '2026-03-23T13:33:00Z'],
    [41.9274, 2.6655, 7322, '2026-03-23T13:34:30Z'],
    [41.841, 2.5816, 4486, '2026-03-23T13:36:00Z'],
    [41.7487, 2.4984, 2114, '2026-03-23T13:37:30Z'],
    [41.6585, 2.4147, 842, '2026-03-23T13:39:00Z'],
    [41.5688, 2.3291, 312, '2026-03-23T13:40:30Z'],
    [41.4776, 2.245, 107, '2026-03-23T13:42:00Z'],
    [41.3885, 2.161, 43, '2026-03-23T13:43:30Z'],
    [41.2981, 2.0785, -2, '2026-03-23T13:45:00Z'],
    [41.2971, 2.0785, 4, '2026-03-23T13:45:00Z'],
  ]),

  'sample-aircraft-10': trackWithAltitude([
    [60.3193, 24.964, 58, '2026-03-23T13:00:00Z'],
    [60.61, 25.7089, 61, '2026-03-23T13:04:00Z'],
    [60.8989, 26.4661, 164, '2026-03-23T13:08:00Z'],
    [61.1831, 27.2353, 1461, '2026-03-23T13:12:00Z'],
    [61.463, 28.0198, 7885, '2026-03-23T13:16:00Z'],
    [61.7391, 28.8216, 11488, '2026-03-23T13:20:00Z'],
    [62.0089, 29.6346, 11898, '2026-03-23T13:24:00Z'],
    [62.2721, 30.4623, 11899, '2026-03-23T13:28:00Z'],
    [62.5347, 31.3048, 11896, '2026-03-23T13:32:00Z'],
    [62.7913, 32.164, 11897, '2026-03-23T13:36:00Z'],
    [63.042, 33.035, 11897, '2026-03-23T13:40:00Z'],
    [63.2852, 33.9213, 11898, '2026-03-23T13:44:00Z'],
    [63.525, 34.8245, 11903, '2026-03-23T13:48:00Z'],
    [63.7598, 35.7427, 11910, '2026-03-23T13:52:00Z'],
    [63.9848, 36.6745, 11901, '2026-03-23T13:56:00Z'],
    [64.2073, 37.6231, 11894, '2026-03-23T14:00:00Z'],
    [64.4236, 38.5848, 11894, '2026-03-23T14:04:00Z'],
    [64.6303, 39.5629, 11896, '2026-03-23T14:08:00Z'],
    [64.8333, 40.5551, 11893, '2026-03-23T14:12:00Z'],
    [65.03, 41.5624, 11909, '2026-03-23T14:16:00Z'],
    [65.2184, 42.5829, 11894, '2026-03-23T14:20:00Z'],
    [65.4013, 43.6206, 11894, '2026-03-23T14:24:00Z'],
    [65.5743, 44.6697, 11902, '2026-03-23T14:28:00Z'],
    [65.7413, 45.7345, 11891, '2026-03-23T14:32:00Z'],
    [65.8992, 46.812, 11903, '2026-03-23T14:36:00Z'],
    [66.0507, 47.9044, 11892, '2026-03-23T14:40:00Z'],
    [66.1961, 49.0081, 11898, '2026-03-23T14:44:00Z'],
    [66.3319, 50.1235, 11887, '2026-03-23T14:48:00Z'],
    [66.4605, 51.251, 11905, '2026-03-23T14:52:00Z'],
    [66.5801, 52.3909, 11908, '2026-03-23T14:56:00Z'],
    [66.694, 53.5407, 11899, '2026-03-23T15:00:00Z'],
    [66.7955, 54.6988, 11910, '2026-03-23T15:04:00Z'],
    [66.8914, 55.8692, 11899, '2026-03-23T15:08:00Z'],
    [66.9757, 57.0478, 11894, '2026-03-23T15:12:00Z'],
    [67.051, 58.2331, 11895, '2026-03-23T15:16:00Z'],
    [67.1214, 59.426, 11894, '2026-03-23T15:20:00Z'],
    [67.1795, 60.6247, 11907, '2026-03-23T15:24:00Z'],
    [67.2315, 61.829, 11905, '2026-03-23T15:28:00Z'],
    [67.2719, 63.0365, 11897, '2026-03-23T15:32:00Z'],
    [67.3023, 64.2525, 11899, '2026-03-23T15:36:00Z'],
    [67.3259, 65.4654, 11909, '2026-03-23T15:40:00Z'],
    [67.3392, 66.6859, 11907, '2026-03-23T15:44:00Z'],
    [67.3431, 67.9025, 11894, '2026-03-23T15:48:00Z'],
    [67.3387, 69.1202, 11899, '2026-03-23T15:52:00Z'],
    [67.3254, 70.3381, 11898, '2026-03-23T15:56:00Z'],
    [67.3019, 71.5551, 11903, '2026-03-23T16:00:00Z'],
    [67.2693, 72.7672, 11906, '2026-03-23T16:04:00Z'],
    [67.2293, 73.9771, 11889, '2026-03-23T16:08:00Z'],
    [67.1775, 75.1805, 11902, '2026-03-23T16:12:00Z'],
    [67.1185, 76.3796, 11902, '2026-03-23T16:16:00Z'],
    [67.05, 77.573, 11902, '2026-03-23T16:20:00Z'],
    [66.9741, 78.7589, 11899, '2026-03-23T16:24:00Z'],
    [66.8865, 79.9347, 11899, '2026-03-23T16:28:00Z'],
    [66.7904, 81.1048, 11905, '2026-03-23T16:32:00Z'],
    [66.6882, 82.2639, 11905, '2026-03-23T16:36:00Z'],
    [66.5769, 83.414, 11905, '2026-03-23T16:40:00Z'],
    [66.456, 84.553, 11898, '2026-03-23T16:44:00Z'],
    [66.3267, 85.6789, 11903, '2026-03-23T16:48:00Z'],
    [66.1916, 86.7933, 11904, '2026-03-23T16:52:00Z'],
    [66.0478, 87.8984, 11902, '2026-03-23T16:56:00Z'],
    [65.8922, 88.9894, 11904, '2026-03-23T17:00:00Z'],
    [65.736, 90.0676, 11902, '2026-03-23T17:04:00Z'],
    [65.5694, 91.1298, 11901, '2026-03-23T17:08:00Z'],
    [65.3916, 92.181, 11899, '2026-03-23T17:12:00Z'],
    [65.2108, 93.2189, 11906, '2026-03-23T17:16:00Z'],
    [65.023, 94.2386, 11904, '2026-03-23T17:20:00Z'],
    [64.8285, 95.2426, 11911, '2026-03-23T17:24:00Z'],
    [64.6266, 96.2359, 11904, '2026-03-23T17:28:00Z'],
    [64.4149, 97.2142, 11902, '2026-03-23T17:32:00Z'],
    [64.1996, 98.176, 11901, '2026-03-23T17:36:00Z'],
    [63.9786, 99.1229, 11901, '2026-03-23T17:40:00Z'],
    [63.7512, 100.054, 11896, '2026-03-23T17:44:00Z'],
    [63.5173, 100.9733, 11896, '2026-03-23T17:48:00Z'],
    [63.2793, 101.8712, 11902, '2026-03-23T17:52:00Z'],
    [63.0326, 102.7583, 11895, '2026-03-23T17:56:00Z'],
    [62.781, 103.6318, 11902, '2026-03-23T18:00:00Z'],
    [62.5268, 104.4886, 11907, '2026-03-23T18:04:00Z'],
    [62.2656, 105.3322, 11908, '2026-03-23T18:08:00Z'],
    [61.9987, 106.1604, 11904, '2026-03-23T18:12:00Z'],
    [61.7305, 106.9717, 11904, '2026-03-23T18:16:00Z'],
    [61.4553, 107.7714, 11901, '2026-03-23T18:20:00Z'],
    [61.1738, 108.5552, 11903, '2026-03-23T18:24:00Z'],
    [60.888, 109.326, 11896, '2026-03-23T18:28:00Z'],
    [60.6009, 110.0835, 11896, '2026-03-23T18:32:00Z'],
    [60.3059, 110.8272, 11897, '2026-03-23T18:36:00Z'],
    [60.0115, 111.5569, 11906, '2026-03-23T18:40:00Z'],
    [59.7093, 112.2734, 11909, '2026-03-23T18:44:00Z'],
    [59.4041, 112.979, 11898, '2026-03-23T18:48:00Z'],
    [59.0958, 113.6719, 11900, '2026-03-23T18:52:00Z'],
    [58.7817, 114.3498, 11896, '2026-03-23T18:56:00Z'],
    [58.4687, 115.0165, 11902, '2026-03-23T19:00:00Z'],
    [58.15, 115.6735, 11898, '2026-03-23T19:04:00Z'],
    [57.8259, 116.3173, 11899, '2026-03-23T19:08:00Z'],
    [57.4994, 116.9482, 11892, '2026-03-23T19:12:00Z'],
    [57.1728, 117.5698, 11899, '2026-03-23T19:16:00Z'],
    [56.84, 118.179, 11903, '2026-03-23T19:20:00Z'],
    [56.504, 118.7768, 11899, '2026-03-23T19:24:00Z'],
    [56.168, 119.3657, 11896, '2026-03-23T19:28:00Z'],
    [55.8277, 119.9439, 11899, '2026-03-23T19:32:00Z'],
    [55.4837, 120.5119, 11901, '2026-03-23T19:36:00Z'],
    [55.1404, 121.071, 11909, '2026-03-23T19:40:00Z'],
    [54.7896, 121.6207, 11900, '2026-03-23T19:44:00Z'],
    [54.44, 122.1565, 11905, '2026-03-23T19:48:00Z'],
    [54.0885, 122.6898, 11898, '2026-03-23T19:52:00Z'],
    [53.7327, 123.209, 11906, '2026-03-23T19:56:00Z'],
    [53.3748, 123.7228, 11896, '2026-03-23T20:00:00Z'],
    [53.0153, 124.225, 11905, '2026-03-23T20:04:00Z'],
    [52.6544, 124.7207, 11907, '2026-03-23T20:08:00Z'],
    [52.2894, 125.2076, 11905, '2026-03-23T20:12:00Z'],
    [51.9242, 125.6859, 11890, '2026-03-23T20:16:00Z'],
    [51.5555, 126.1584, 11899, '2026-03-23T20:20:00Z'],
    [51.1875, 126.6228, 11894, '2026-03-23T20:24:00Z'],
    [50.8171, 127.0797, 11895, '2026-03-23T20:28:00Z'],
    [50.4441, 127.5295, 11893, '2026-03-23T20:32:00Z'],
    [50.0702, 127.9717, 11900, '2026-03-23T20:36:00Z'],
    [49.6907, 128.4073, 11908, '2026-03-23T20:40:00Z'],
    [49.315, 128.8372, 11905, '2026-03-23T20:44:00Z'],
    [48.9346, 129.2577, 11900, '2026-03-23T20:48:00Z'],
    [48.5526, 129.6736, 11902, '2026-03-23T20:52:00Z'],
    [48.1717, 130.0855, 11903, '2026-03-23T20:56:00Z'],
    [47.7862, 130.4876, 11895, '2026-03-23T21:00:00Z'],
    [47.402, 130.8838, 11899, '2026-03-23T21:04:00Z'],
    [47.0146, 131.2768, 11900, '2026-03-23T21:08:00Z'],
    [46.6287, 131.6631, 11903, '2026-03-23T21:12:00Z'],
    [46.2395, 132.0441, 11903, '2026-03-23T21:16:00Z'],
    [45.8479, 132.4194, 11899, '2026-03-23T21:20:00Z'],
    [45.457, 132.7877, 11894, '2026-03-23T21:24:00Z'],
    [45.0634, 133.1543, 11898, '2026-03-23T21:28:00Z'],
    [44.6709, 133.5128, 11900, '2026-03-23T21:32:00Z'],
    [44.274, 133.8704, 11906, '2026-03-23T21:36:00Z'],
    [43.8802, 134.2199, 11903, '2026-03-23T21:40:00Z'],
    [43.4816, 134.5643, 11897, '2026-03-23T21:44:00Z'],
    [43.0815, 134.9044, 11903, '2026-03-23T21:48:00Z'],
    [42.6822, 135.2413, 11901, '2026-03-23T21:52:00Z'],
    [42.2828, 135.5733, 11896, '2026-03-23T21:56:00Z'],
    [41.8836, 135.9048, 11900, '2026-03-23T22:00:00Z'],
    [41.4782, 136.2272, 11898, '2026-03-23T22:04:00Z'],
    [41.0765, 136.5485, 11899, '2026-03-23T22:08:00Z'],
    [40.6723, 136.8652, 11898, '2026-03-23T22:12:00Z'],
    [40.2671, 137.1762, 11904, '2026-03-23T22:16:00Z'],
    [39.8625, 137.4881, 11903, '2026-03-23T22:20:00Z'],
    [39.4553, 137.7907, 11891, '2026-03-23T22:24:00Z'],
    [39.0473, 138.0928, 11899, '2026-03-23T22:28:00Z'],
    [38.6403, 138.3911, 11894, '2026-03-23T22:32:00Z'],
    [38.2321, 138.6836, 11645, '2026-03-23T22:36:00Z'],
    [37.8211, 138.9742, 9962, '2026-03-23T22:40:00Z'],
    [37.4123, 139.264, 4393, '2026-03-23T22:44:00Z'],
    [37.003, 139.5482, 771, '2026-03-23T22:48:00Z'],
    [36.5905, 139.8311, 123, '2026-03-23T22:52:00Z'],
    [36.1774, 140.1101, 35, '2026-03-23T22:56:00Z'],
    [35.7652, 140.3872, 39, '2026-03-23T23:00:00Z'],
    [35.7647, 140.3864, 41, '2026-03-23T23:00:00Z'],
  ]),

  'sample-aircraft-11': trackWithAltitude([
    [51.47, -0.4551, 23, '2026-03-23T14:00:00Z'],
    [51.5828, 0.4001, 25, '2026-03-23T14:04:00Z'],
    [51.6872, 1.2591, 76, '2026-03-23T14:08:00Z'],
    [51.7859, 2.1217, 850, '2026-03-23T14:12:00Z'],
    [51.8805, 2.9892, 6642, '2026-03-23T14:16:00Z'],
    [51.9672, 3.86, 11123, '2026-03-23T14:20:00Z'],
    [52.0484, 4.7333, 11591, '2026-03-23T14:24:00Z'],
    [52.1218, 5.6103, 11606, '2026-03-23T14:28:00Z'],
    [52.1894, 6.4896, 11597, '2026-03-23T14:32:00Z'],
    [52.2515, 7.3719, 11598, '2026-03-23T14:36:00Z'],
    [52.3049, 8.2557, 11606, '2026-03-23T14:40:00Z'],
    [52.3529, 9.1403, 11601, '2026-03-23T14:44:00Z'],
    [52.3928, 10.0286, 11597, '2026-03-23T14:48:00Z'],
    [52.4259, 10.9203, 11599, '2026-03-23T14:52:00Z'],
    [52.4556, 11.8099, 11600, '2026-03-23T14:56:00Z'],
    [52.475, 12.7019, 11590, '2026-03-23T15:00:00Z'],
    [52.4916, 13.5932, 11595, '2026-03-23T15:04:00Z'],
    [52.4987, 14.4863, 11593, '2026-03-23T15:08:00Z'],
    [52.498, 15.3805, 11600, '2026-03-23T15:12:00Z'],
    [52.492, 16.2722, 11601, '2026-03-23T15:16:00Z'],
    [52.4804, 17.1648, 11598, '2026-03-23T15:20:00Z'],
    [52.46, 18.0564, 11602, '2026-03-23T15:24:00Z'],
    [52.4339, 18.9475, 11595, '2026-03-23T15:28:00Z'],
    [52.4021, 19.8388, 11596, '2026-03-23T15:32:00Z'],
    [52.3632, 20.7264, 11595, '2026-03-23T15:36:00Z'],
    [52.3148, 21.6133, 11602, '2026-03-23T15:40:00Z'],
    [52.2631, 22.4971, 11596, '2026-03-23T15:44:00Z'],
    [52.2042, 23.3809, 11597, '2026-03-23T15:48:00Z'],
    [52.1369, 24.2585, 11605, '2026-03-23T15:52:00Z'],
    [52.0656, 25.136, 11590, '2026-03-23T15:56:00Z'],
    [51.9859, 26.0113, 11602, '2026-03-23T16:00:00Z'],
    [51.9015, 26.8811, 11606, '2026-03-23T16:04:00Z'],
    [51.8109, 27.7497, 11607, '2026-03-23T16:08:00Z'],
    [51.7133, 28.6129, 11609, '2026-03-23T16:12:00Z'],
    [51.608, 29.4736, 11601, '2026-03-23T16:16:00Z'],
    [51.4965, 30.3301, 11593, '2026-03-23T16:20:00Z'],
    [51.3827, 31.1806, 11598, '2026-03-23T16:24:00Z'],
    [51.2586, 32.0312, 11590, '2026-03-23T16:28:00Z'],
    [51.1288, 32.8709, 11604, '2026-03-23T16:32:00Z'],
    [50.9929, 33.7103, 11603, '2026-03-23T16:36:00Z'],
    [50.8498, 34.5427, 11595, '2026-03-23T16:40:00Z'],
    [50.7049, 35.3703, 11602, '2026-03-23T16:44:00Z'],
    [50.5521, 36.1925, 11594, '2026-03-23T16:48:00Z'],
    [50.3941, 37.0119, 11592, '2026-03-23T16:52:00Z'],
    [50.2304, 37.8199, 11599, '2026-03-23T16:56:00Z'],
    [50.0599, 38.6286, 11598, '2026-03-23T17:00:00Z'],
    [49.8832, 39.4267, 11596, '2026-03-23T17:04:00Z'],
    [49.7032, 40.2221, 11599, '2026-03-23T17:08:00Z'],
    [49.517, 41.0103, 11598, '2026-03-23T17:12:00Z'],
    [49.3258, 41.7907, 11602, '2026-03-23T17:16:00Z'],
    [49.1272, 42.5688, 11599, '2026-03-23T17:20:00Z'],
    [48.9265, 43.3379, 11593, '2026-03-23T17:24:00Z'],
    [48.721, 44.1005, 11600, '2026-03-23T17:28:00Z'],
    [48.5065, 44.8594, 11598, '2026-03-23T17:32:00Z'],
    [48.2911, 45.6101, 11605, '2026-03-23T17:36:00Z'],
    [48.0695, 46.3548, 11601, '2026-03-23T17:40:00Z'],
    [47.8432, 47.0929, 11606, '2026-03-23T17:44:00Z'],
    [47.6111, 47.8232, 11601, '2026-03-23T17:48:00Z'],
    [47.3762, 48.5491, 11605, '2026-03-23T17:52:00Z'],
    [47.1362, 49.2672, 11595, '2026-03-23T17:56:00Z'],
    [46.8895, 49.9785, 11601, '2026-03-23T18:00:00Z'],
    [46.6407, 50.6847, 11599, '2026-03-23T18:04:00Z'],
    [46.3878, 51.3821, 11605, '2026-03-23T18:08:00Z'],
    [46.1324, 52.0755, 11602, '2026-03-23T18:12:00Z'],
    [45.8687, 52.7623, 11602, '2026-03-23T18:16:00Z'],
    [45.6037, 53.4401, 11601, '2026-03-23T18:20:00Z'],
    [45.3331, 54.1138, 11605, '2026-03-23T18:24:00Z'],
    [45.0579, 54.7815, 11587, '2026-03-23T18:28:00Z'],
    [44.781, 55.4381, 11597, '2026-03-23T18:32:00Z'],
    [44.5005, 56.0928, 11599, '2026-03-23T18:36:00Z'],
    [44.2152, 56.7416, 11598, '2026-03-23T18:40:00Z'],
    [43.926, 57.3832, 11600, '2026-03-23T18:44:00Z'],
    [43.6325, 58.0165, 11605, '2026-03-23T18:48:00Z'],
    [43.3383, 58.646, 11611, '2026-03-23T18:52:00Z'],
    [43.0397, 59.268, 11603, '2026-03-23T18:56:00Z'],
    [42.7368, 59.8855, 11597, '2026-03-23T19:00:00Z'],
    [42.4311, 60.4948, 11593, '2026-03-23T19:04:00Z'],
    [42.1216, 61.1005, 11599, '2026-03-23T19:08:00Z'],
    [41.8092, 61.701, 11600, '2026-03-23T19:12:00Z'],
    [41.4965, 62.2924, 11601, '2026-03-23T19:16:00Z'],
    [41.1752, 62.8787, 11601, '2026-03-23T19:20:00Z'],
    [40.8527, 63.4607, 11605, '2026-03-23T19:24:00Z'],
    [40.5308, 64.0358, 11602, '2026-03-23T19:28:00Z'],
    [40.2041, 64.6067, 11606, '2026-03-23T19:32:00Z'],
    [39.8738, 65.1699, 11597, '2026-03-23T19:36:00Z'],
    [39.542, 65.7309, 11600, '2026-03-23T19:40:00Z'],
    [39.206, 66.285, 11601, '2026-03-23T19:44:00Z'],
    [38.8683, 66.8328, 11602, '2026-03-23T19:48:00Z'],
    [38.5291, 67.3758, 11596, '2026-03-23T19:52:00Z'],
    [38.1846, 67.915, 11603, '2026-03-23T19:56:00Z'],
    [37.8408, 68.4474, 11605, '2026-03-23T20:00:00Z'],
    [37.4934, 68.9753, 11596, '2026-03-23T20:04:00Z'],
    [37.1451, 69.4996, 11598, '2026-03-23T20:08:00Z'],
    [36.7916, 70.017, 11592, '2026-03-23T20:12:00Z'],
    [36.4363, 70.5294, 11602, '2026-03-23T20:16:00Z'],
    [36.0815, 71.0403, 11596, '2026-03-23T20:20:00Z'],
    [35.7215, 71.5435, 11593, '2026-03-23T20:24:00Z'],
    [35.3612, 72.0434, 11601, '2026-03-23T20:28:00Z'],
    [34.9993, 72.538, 11602, '2026-03-23T20:32:00Z'],
    [34.6353, 73.0303, 11599, '2026-03-23T20:36:00Z'],
    [34.2679, 73.5174, 11598, '2026-03-23T20:40:00Z'],
    [33.9, 74.0009, 11600, '2026-03-23T20:44:00Z'],
    [33.5303, 74.4782, 11600, '2026-03-23T20:48:00Z'],
    [33.1577, 74.9521, 11605, '2026-03-23T20:52:00Z'],
    [32.7814, 75.4229, 11606, '2026-03-23T20:56:00Z'],
    [32.4056, 75.89, 11590, '2026-03-23T21:00:00Z'],
    [32.0288, 76.3504, 11600, '2026-03-23T21:04:00Z'],
    [31.6476, 76.8084, 11595, '2026-03-23T21:08:00Z'],
    [31.2695, 77.2659, 11599, '2026-03-23T21:12:00Z'],
    [30.8855, 77.7154, 11597, '2026-03-23T21:16:00Z'],
    [30.5024, 78.1641, 11595, '2026-03-23T21:20:00Z'],
    [30.1152, 78.608, 11595, '2026-03-23T21:24:00Z'],
    [29.7291, 79.0485, 11597, '2026-03-23T21:28:00Z'],
    [29.3407, 79.4863, 11603, '2026-03-23T21:32:00Z'],
    [28.9509, 79.9199, 11607, '2026-03-23T21:36:00Z'],
    [28.5589, 80.3517, 11606, '2026-03-23T21:40:00Z'],
    [28.1666, 80.7753, 11598, '2026-03-23T21:44:00Z'],
    [27.7718, 81.2028, 11596, '2026-03-23T21:48:00Z'],
    [27.3785, 81.624, 11595, '2026-03-23T21:52:00Z'],
    [26.9812, 82.0414, 11602, '2026-03-23T21:56:00Z'],
    [26.5845, 82.457, 11601, '2026-03-23T22:00:00Z'],
    [26.185, 82.8703, 11601, '2026-03-23T22:04:00Z'],
    [25.7858, 83.2774, 11604, '2026-03-23T22:08:00Z'],
    [25.3845, 83.6847, 11595, '2026-03-23T22:12:00Z'],
    [24.9828, 84.0895, 11598, '2026-03-23T22:16:00Z'],
    [24.5787, 84.4914, 11600, '2026-03-23T22:20:00Z'],
    [24.1752, 84.8891, 11599, '2026-03-23T22:24:00Z'],
    [23.7711, 85.2864, 11599, '2026-03-23T22:28:00Z'],
    [23.3642, 85.679, 11600, '2026-03-23T22:32:00Z'],
    [22.9546, 86.0719, 11598, '2026-03-23T22:36:00Z'],
    [22.5462, 86.461, 11603, '2026-03-23T22:40:00Z'],
    [22.1372, 86.848, 11598, '2026-03-23T22:44:00Z'],
    [21.7286, 87.2335, 11598, '2026-03-23T22:48:00Z'],
    [21.3169, 87.6165, 11602, '2026-03-23T22:52:00Z'],
    [20.9039, 87.9959, 11603, '2026-03-23T22:56:00Z'],
    [20.4918, 88.3747, 11601, '2026-03-23T23:00:00Z'],
    [20.078, 88.7507, 11598, '2026-03-23T23:04:00Z'],
    [19.6649, 89.1231, 11601, '2026-03-23T23:08:00Z'],
    [19.2487, 89.4972, 11605, '2026-03-23T23:12:00Z'],
    [18.8339, 89.8666, 11598, '2026-03-23T23:16:00Z'],
    [18.4163, 90.2352, 11603, '2026-03-23T23:20:00Z'],
    [17.9981, 90.6022, 11606, '2026-03-23T23:24:00Z'],
    [17.5823, 90.9672, 11614, '2026-03-23T23:28:00Z'],
    [17.1622, 91.3316, 11605, '2026-03-23T23:32:00Z'],
    [16.7407, 91.6922, 11601, '2026-03-23T23:36:00Z'],
    [16.3226, 92.0502, 11603, '2026-03-23T23:40:00Z'],
    [15.9029, 92.4116, 11606, '2026-03-23T23:44:00Z'],
    [15.4822, 92.7688, 11592, '2026-03-23T23:48:00Z'],
    [15.0588, 93.1256, 11605, '2026-03-23T23:52:00Z'],
    [14.6373, 93.4771, 11598, '2026-03-23T23:56:00Z'],
    [14.2147, 93.831, 11604, '2026-03-24T00:00:00Z'],
    [13.7933, 94.1837, 11600, '2026-03-24T00:04:00Z'],
    [13.3674, 94.5331, 11599, '2026-03-24T00:08:00Z'],
    [12.9441, 94.8823, 11603, '2026-03-24T00:12:00Z'],
    [12.5176, 95.23, 11605, '2026-03-24T00:16:00Z'],
    [12.0929, 95.5741, 11608, '2026-03-24T00:20:00Z'],
    [11.6674, 95.9236, 11590, '2026-03-24T00:24:00Z'],
    [11.2395, 96.2656, 11603, '2026-03-24T00:28:00Z'],
    [10.8142, 96.6088, 11597, '2026-03-24T00:32:00Z'],
    [10.3866, 96.9512, 11597, '2026-03-24T00:36:00Z'],
    [9.9597, 97.2942, 11597, '2026-03-24T00:40:00Z'],
    [9.5351, 97.635, 11601, '2026-03-24T00:44:00Z'],
    [9.1061, 97.9722, 11607, '2026-03-24T00:48:00Z'],
    [8.6782, 98.3141, 11596, '2026-03-24T00:52:00Z'],
    [8.2474, 98.6531, 11599, '2026-03-24T00:56:00Z'],
    [7.8195, 98.9896, 11596, '2026-03-24T01:00:00Z'],
    [7.3913, 99.3261, 11599, '2026-03-24T01:04:00Z'],
    [6.962, 99.6639, 11609, '2026-03-24T01:08:00Z'],
    [6.5309, 99.9985, 11595, '2026-03-24T01:12:00Z'],
    [6.1021, 100.334, 11599, '2026-03-24T01:16:00Z'],
    [5.6723, 100.6668, 11599, '2026-03-24T01:20:00Z'],
    [5.2426, 101.0028, 11604, '2026-03-24T01:24:00Z'],
    [4.8114, 101.3368, 11603, '2026-03-24T01:28:00Z'],
    [4.3814, 101.6681, 11608, '2026-03-24T01:32:00Z'],
    [3.95, 102.0012, 11324, '2026-03-24T01:36:00Z'],
    [3.5214, 102.3327, 9273, '2026-03-24T01:40:00Z'],
    [3.0878, 102.6672, 3298, '2026-03-24T01:44:00Z'],
    [2.6568, 102.9992, 452, '2026-03-24T01:48:00Z'],
    [2.2273, 103.3283, 49, '2026-03-24T01:52:00Z'],
    [1.7964, 103.6609, 11, '2026-03-24T01:56:00Z'],
    [1.3653, 103.9912, 15, '2026-03-24T02:00:00Z'],
    [1.3644, 103.9915, 7, '2026-03-24T02:00:00Z'],
  ]),
}
